import { useReadContract, useReadContracts } from "wagmi";
import { formatEther, formatUnits } from "viem";
import { gaiaAbi } from "../lib/abi";
import { GAIA_ADDRESS, DEFAULT_CHAIN_ID, isDeployed } from "../lib/contract";
import { LiquidCard } from "./LiquidCard";
import { InkwellGroup } from "./InkwellGroup";
import { ImagePanel } from "./ImagePanel";

const PHASE_LABELS = ["Awaiting v4 LP", "50 / 50 — team + LP", "100% to LP forever"];
const PHASE_DESC = [
  "owner has not yet wired the v4 position. mints route the LP-half into a buffer until setupV4 is called.",
  "every 0.001 ETH splits down the middle — 50% to the team wallet, 50% to the v4 LP buffer. team half closes at 5 ETH cumulative.",
  "team cap reached. every 0.001 ETH from now on routes to the v4 LP. forever.",
];

export function TokenStats() {
  const chainId = DEFAULT_CHAIN_ID;
  const address = GAIA_ADDRESS[chainId];
  const deployed = isDeployed(chainId);

  const { data: phaseData } = useReadContract({
    chainId, address, abi: gaiaAbi, functionName: "currentPhase",
    query: { enabled: deployed },
  });

  const { data: stats } = useReadContracts({
    contracts: [
      { chainId, address, abi: gaiaAbi, functionName: "totalMints" },
      { chainId, address, abi: gaiaAbi, functionName: "mintsRemaining" },
      { chainId, address, abi: gaiaAbi, functionName: "totalProfitSent" },
      { chainId, address, abi: gaiaAbi, functionName: "totalLPRoutedETH" },
      { chainId, address, abi: gaiaAbi, functionName: "totalLPFlushedETH" },
      { chainId, address, abi: gaiaAbi, functionName: "lpReserveETH" },
      { chainId, address, abi: gaiaAbi, functionName: "totalFeesClaimedETH" },
      { chainId, address, abi: gaiaAbi, functionName: "totalFeesClaimedGAIA" },
      { chainId, address, abi: gaiaAbi, functionName: "totalSupply" },
      { chainId, address, abi: gaiaAbi, functionName: "mintPoolRemaining" },
    ],
    query: { enabled: deployed, refetchInterval: 12_000 },
  });

  const phase = (phaseData as number | undefined) ?? 0;
  const totalMints  = (stats?.[0]?.result as bigint | undefined) ?? 0n;
  const mintsLeft   = (stats?.[1]?.result as bigint | undefined) ?? 210000n;
  const profitSent  = (stats?.[2]?.result as bigint | undefined) ?? 0n;
  const lpRouted    = (stats?.[3]?.result as bigint | undefined) ?? 0n;
  const lpFlushed   = (stats?.[4]?.result as bigint | undefined) ?? 0n;
  const lpReserve   = (stats?.[5]?.result as bigint | undefined) ?? 0n;
  const feesEth     = (stats?.[6]?.result as bigint | undefined) ?? 0n;
  const feesGaia    = (stats?.[7]?.result as bigint | undefined) ?? 0n;
  const supply      = (stats?.[8]?.result as bigint | undefined) ?? 0n;
  const poolLeft    = (stats?.[9]?.result as bigint | undefined) ?? 0n;

  const profitPct = Number(profitSent) / Number(5n * 10n ** 18n);
  const mintPct = Number(totalMints) / 210_000;

  return (
    <section id="waterfall" className="relative px-5 md:px-10 py-24 md:py-32 max-w-7xl mx-auto">
      <div className="relative z-10">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-end mb-12">
          <div>
            <div className="eyebrow mb-4"><span className="dot" />the split</div>
            <h2 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.95]">
              half to the team.{" "}
              <span className="font-serif italic text-aurora">half to the ocean.</span>
            </h2>
            <p className="mt-6 text-bone-dim text-base max-w-2xl">
              21,000 users · 10 mints each · 100,000 GAIA per address. After the first 10,000
              mints the team cap closes — every fee from then on goes to the locked Uniswap v4 LP.
            </p>
          </div>
          <ImagePanel
            src="/images/wallpaper-blue.jpg"
            alt="Sea, ocean, turtles, jellyfish moodboard"
            className="hidden lg:block aspect-[3/4] max-h-80 ml-auto"
            filter="saturate(1.05) contrast(1.05)"
          />
        </div>

        <LiquidCard className="mb-10" variant="ribbon" fill="linear-gradient(135deg, #40b0c2 0%, #3ddc97 60%, #f5c451 100%)">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-1000/80">current phase</div>
              <div className="font-display text-3xl md:text-4xl text-ink-1000">{PHASE_LABELS[phase] ?? "—"}</div>
              <div className="text-ink-1000/80 mt-2 text-sm md:text-base max-w-2xl">{PHASE_DESC[phase] ?? ""}</div>
            </div>
            <div className="font-mono text-ink-1000/80 text-sm">
              {deployed ? `${address.slice(0, 6)}…${address.slice(-4)}` : "not deployed yet"}
            </div>
          </div>
        </LiquidCard>

        <InkwellGroup className="space-y-5">
          <div className="ink-card p-6 md:p-8">
            <div className="flex items-baseline justify-between mb-4">
              <div className="font-display text-2xl">global mint progress</div>
              <div className="font-mono text-sm">
                {totalMints.toString()} <span className="text-bone-dim">/ 210,000 minted · {mintsLeft.toString()} left</span>
              </div>
            </div>
            <div className="h-3 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${Math.max(mintPct, 0.01) * 100}%`, background: "linear-gradient(90deg, #40b0c2, #3ddc97, #f5c451)" }}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="ink-card p-6 md:p-8">
              <PhaseHeader index={1} label="Team — capped at 5 ETH" active={phase === 1} done={phase > 1} />
              <Bar pct={Math.min(profitPct, 1)} from="#ff8a5c" to="#f57e9c" />
              <Row k="paid" v={`${formatEther(profitSent)} ETH`} />
              <Row k="cap" v="5 ETH (= first 10,000 mints)" />
              <Row k="wallet" v="0x7cEc…6614" />
            </div>

            <div className="ink-card p-6 md:p-8">
              <PhaseHeader index={2} label="Uniswap v4 LP — owned by contract" active={phase === 2} done={false} forever />
              <Bar pct={lpRouted > 0n ? Math.min(Number(lpFlushed) / Number(lpRouted), 1) : 0} from="#40b0c2" to="#3ddc97" />
              <Row k="routed total" v={`${formatEther(lpRouted)} ETH`} />
              <Row k="flushed to v4" v={`${formatEther(lpFlushed)} ETH`} />
              <Row k="awaiting flush" v={`${formatEther(lpReserve)} ETH`} />
              <Row k="batches every" v="0.01 ETH (≈ 10 mints)" />
            </div>
          </div>
        </InkwellGroup>

        <InkwellGroup className="mt-12 grid sm:grid-cols-2 md:grid-cols-4 gap-5">
          <MicroStat k="total mints" v={`${totalMints.toString()} / 210,000`} />
          <MicroStat k="circulating supply" v={`${humanShort(formatUnits(supply - poolLeft, 18))} GAIA`} />
          <MicroStat k="locked in contract" v={`${humanShort(formatUnits(poolLeft, 18))} GAIA`} />
          <MicroStat
            k="LP fees claimed"
            v={`${Number(formatEther(feesEth)).toFixed(4)} ETH · ${humanShort(formatUnits(feesGaia, 18))} GAIA`}
          />
        </InkwellGroup>
      </div>
    </section>
  );
}

function PhaseHeader({
  index, label, active, done, forever,
}: { index: number; label: string; active: boolean; done: boolean; forever?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={`size-7 rounded-full grid place-items-center font-mono text-xs ${active ? "bg-teal-400 text-ink-1000" : done ? "bg-teal-400/30 text-teal-300" : "bg-white/5 text-bone-dim"}`}>
          {done ? "✓" : index}
        </div>
        <div className="font-display text-2xl">{label}</div>
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-dim">
        {forever ? "permanent" : active ? "active" : done ? "complete" : "upcoming"}
      </div>
    </div>
  );
}

function Bar({ pct, from, to }: { pct: number; from: string; to: string }) {
  return (
    <div className="my-5 h-2 rounded-full bg-white/5 overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${Math.max(pct, 0.02) * 100}%`, background: `linear-gradient(90deg, ${from}, ${to})` }}
      />
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-white/5 last:border-b-0">
      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-bone-dim">{k}</span>
      <span className="font-mono text-sm">{v}</span>
    </div>
  );
}

function MicroStat({ k, v }: { k: string; v: string }) {
  return (
    <div className="ink-card p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-dim">{k}</div>
      <div className="mt-2 font-display text-xl">{v}</div>
    </div>
  );
}

function humanShort(s: string) {
  const n = Number(s);
  if (!isFinite(n)) return s;
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
